import PropTypes from "prop-types";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  BufferService,
  LoadingContext,
  M_StateActions,
  MSelect,
  MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the unified Tender CRUD page in Phase mode.
 */
const TENDER_PHASE_CRUD_ROUTE = "tenders/TenderConfigCrud";

/**
 * Backend filter used to load only Non-Spot Tenders for phase selection.
 */
const NON_SPOT_TENDER_SELECTOR_QUERY =
  "?page=1&pagesize=1000&filter=TenderTypeId%20eq%202";

//endregion

//region Helpers

/**
 * Normalizes a paged API response or raw array into a list.
 *
 * Purpose:
 * - supports Items and items casing;
 * - supports raw array payloads;
 * - keeps the select mapping resilient to small API shape changes.
 *
 * @param {object|Array|null|undefined} data - API response payload.
 * @returns {Array<object>} Normalized list.
 */
const normalizeList = (data) => {
  if (!data) return [];

  const list = Array.isArray(data)
    ? data
    : data.Items ?? data.items ?? [];

  return Array.isArray(list) ? list : [];
};

/**
 * Reads an entity id using PascalCase or camelCase payloads.
 *
 * @param {object|null|undefined} row - Entity row.
 * @returns {number|string|null} Entity id.
 */
const getRowId = (row) => row?.Id ?? row?.id ?? null;

/**
 * Reads an entity display name using PascalCase or camelCase payloads.
 *
 * @param {object|null|undefined} row - Entity row.
 * @returns {string} Entity display name.
 */
const getRowName = (row) => row?.Name ?? row?.name ?? "";

//endregion

//region Component

/**
 * Renders the Tender Phases configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - load Non-Spot Tenders into a selector;
 * - persist the selected Tender per tab;
 * - render TenderPhases filtered by the selected Tender;
 * - expose View/Edit phase workflows through the unified TenderConfigCrud route;
 * - persist phase-mode state before opening the CRUD tab.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Tender Phases configuration tab.
 */
export default function TenderPhaseConfig({
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const stateValues = useSelector((state) => state.StateKeyValue);

  const [id, setId] = useState(() =>
    stateValues.find((obj) => obj.key === "selectedTab")?.value
  );

  const [tenders, setTenders] = useState([]);
  const [selectedTenderId, setSelectedTenderId] = useState(null);
  const [refresh, setRefresh] = useState(false);

  /**
   * Removes table actions that are not required in this tab.
   *
   * Important:
   * - TenderPhase creation is not exposed here;
   * - Copy is disabled to keep phase handling explicit;
   * - Print is not part of the desired workflow.
   */
  const removeContextItems = useMemo(
    () => ["Print", "Copy", "Create"],
    []
  );

  const tenderService = useMemo(
    () => new BufferService("Tenders", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  /**
   * Upserts one key/value pair into the current tab session state.
   *
   * Purpose:
   * - keeps selectedTenderId isolated per tab;
   * - avoids collisions when multiple Tender tabs are open.
   *
   * @param {number|string} tabId - Current tab id.
   * @param {{key: string, value: any}} entry - Session entry to persist.
   * @returns {void}
   */
  const upsertSession = useCallback(
    (tabId, entry) => {
      const current =
        stateValues.find((obj) => obj.key === tabId)?.value || [];

      const filtered = current.filter((item) => item.key !== entry.key);

      dispatch(
        M_StateActions.addM_State({
          key: tabId,
          value: [...filtered, entry],
        })
      );
    },
    [dispatch, stateValues]
  );

  /**
   * Loads the Non-Spot Tender selector options.
   */
  useEffect(() => {
    ctx.setIsLoad?.(true);

    tenderService
      .filter(NON_SPOT_TENDER_SELECTOR_QUERY)
      .then((data) => {
        if (data?.__ok === false || data?.__isApiError) {
          throw data;
        }

        const options = normalizeList(data)
          .map((tender) => ({
            label: getRowName(tender),
            value: getRowId(tender),
          }))
          .filter((option) => option.value != null);

        setTenders(options);

        const session = stateValues.find((obj) => obj.key === id)?.value;
        const savedTenderId = session?.find(
          (item) => item.key === "selectedTenderId"
        )?.value;

        if (
          savedTenderId != null &&
          options.some((option) => option.value === savedTenderId)
        ) {
          setSelectedTenderId(savedTenderId);
        }
      })
      .catch(() => {
        toastMessages("error", "", t("ErrorLoadingTenders"));
      })
      .finally(() => {
        ctx.setIsLoad?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, tenderService]);

  /**
   * Restores tab-scoped state when the selected application tab changes.
   */
  useEffect(() => {
    const newId = stateValues.find((obj) => obj.key === "selectedTab")?.value;

    if (!newId || newId === id) return;

    setId(newId);

    const session = stateValues.find((obj) => obj.key === newId)?.value;
    const savedTenderId = session?.find(
      (item) => item.key === "selectedTenderId"
    )?.value;
    const savedRefresh = session?.find(
      (item) => item.key === "refresh"
    )?.value;

    if (savedTenderId != null) {
      setSelectedTenderId(savedTenderId);
    }

    if (savedRefresh !== undefined) {
      setRefresh(savedRefresh);
    }
  }, [id, stateValues]);

  /**
   * Persists selected Tender per tab whenever it changes.
   */
  useEffect(() => {
    if (!id) return;

    upsertSession(id, {
      key: "selectedTenderId",
      value: selectedTenderId ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenderId]);

  /**
   * Persists refresh state per tab.
   */
  useEffect(() => {
    if (!id) return;

    upsertSession(id, {
      key: "refresh",
      value: refresh,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh]);

  /**
   * Opens the TenderPhase CRUD tab for the requested operation.
   *
   * Flow:
   * - persists the operation in M_State;
   * - validates row selection for View/Edit;
   * - persists the selected TenderPhase row;
   * - enables explicit phase mode through IsPhaseMode;
   * - opens the unified TenderConfigCrud route.
   *
   * @param {"Edit"|"View"} operation - Requested CRUD operation.
   * @param {object|null|undefined} row - Selected TenderPhase row.
   * @param {string} label - Localized operation label.
   * @returns {void}
   */
  const openTenderPhaseCrud = useCallback(
    (operation, row, label) => {
      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      if (["Edit", "View"].includes(operation)) {
        if (!row) {
          toastMessages("error", "", t("SelectedLine"));
          return;
        }

        dispatch(
          M_StateActions.addM_State({
            key: "TenderPhaseLine",
            value: row,
          })
        );

        dispatch(
          M_StateActions.addM_State({
            key: "IsPhaseMode",
            value: true,
          })
        );
      }

      const safeName = row?.Name ?? row?.name ?? "";
      const tabLabel = `${label} "${safeName}"`;

      onCreateTab(TENDER_PHASE_CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the tab-based TenderPhase workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        [
          "View",
          (row) =>
            openTenderPhaseCrud(
              "View",
              row,
              t("LabelViewTenderPhase")
            ),
        ],
        [
          "Edit",
          (row) =>
            openTenderPhaseCrud(
              "Edit",
              row,
              t("LabelEditTenderPhase")
            ),
        ],
      ]),
    [openTenderPhaseCrud, t]
  );

  return (
    <div
      className="config-page config-page--tabs"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header config-page__header--tabs">
        <div className="config-page__title config-page__title--tabs">
          {t("TenderPhases")}
        </div>

        <div className="config-page__description config-page__description--tabs">
          {t("InfoTenderPhases")}
        </div>
      </div>

      <div className="config-page__controls config-page__controls--tabs">
        <div className="config-page__control config-page__control--tabs">
          <div style={{ margin: "10px 0 0 14px" }}>
            <MSelect
              label={t("SelectTender")}
              value={
                tenders.find(
                  (tender) => tender.value === selectedTenderId
                ) || null
              }
              options={tenders}
              onChangeValue={(option) =>
                setSelectedTenderId(option?.value ?? null)
              }
              placeholder={t("SelectTenderPlaceholder")}
              required
              autocomplete
            />
          </div>
        </div>
      </div>

      {selectedTenderId && (
        <div className="config-page__table config-page__table--tabs">
          <MTable
            key={`TenderPhases-${selectedTenderId}`}
            filtersOnTable
            entityName="TenderPhases"
            roleName="Tenders"
            rolePermissions={roles?.map((role) => role.Value)}
            removeContextItems={removeContextItems}
            refresh={refresh}
            setRefresh={setRefresh}
            useSessionStorage={false}
            FILTER={`TenderId eq ${selectedTenderId}`}
            replaceFunctions={replaceFunctions}
            tabsRailWidth={250}
          />
        </div>
      )}
    </div>
  );
}

//endregion

//region PropTypes

TenderPhaseConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion